// The canonical funnel positions the *records* can put a ticket in. Must stay in step with
// SalesPipelineStageKeys on the API. These are not columns — a company's columns are configured under
// Settings > Sales > Sales Pipeline and each one may claim one of these keys.
export const SALES_PIPELINE_STAGE_KEYS = {
    lead: "Lead",
    quote: "Quote",
    accepted: "Accepted",
    jobScheduled: "Job Scheduled",
    toBeBilled: "To Be Billed",
    awaitingPayment: "Awaiting Payment"
};

// Dropping a card on a Won/Lost column closes the ticket with that result instead of just moving it.
export const SALES_PIPELINE_STAGE_TYPES = {
    open: "Open",
    won: "Won",
    lost: "Lost"
};

export interface ISalesPipelineStageModel {
    salesPipelineStageId: number;
    salesPipelineId: number;
    name: string;
    sortOrder: number;
    color: string;
    // One of SALES_PIPELINE_STAGE_KEYS, or null/'' for a manual-only column.
    derivedStageKey: string;
    stageType: string;
    isActive: boolean;
    // Open tickets currently in this column, so the settings page can warn before a delete.
    ticketCount: number;
}

export class SalesPipelineStageModel implements ISalesPipelineStageModel {
    salesPipelineStageId: number = 0;
    salesPipelineId: number = 0;
    name: string = "";
    sortOrder: number = 0;
    color: string = "#6c757d";
    derivedStageKey: string = null;
    stageType: string = SALES_PIPELINE_STAGE_TYPES.open;
    isActive: boolean = true;
    ticketCount: number = 0;
}

export interface ISalesPipelineModel {
    salesPipelineId: number;
    companyId: number;
    name: string;
    isDefault: boolean;
    isActive: boolean;
    sortOrder: number;
    stages: ISalesPipelineStageModel[];
    // Sent on save only — columns removed in the editor. The API moves any cards on them back to the
    // nearest surviving column rather than orphaning them.
    deletedStageIds: number[];
    ticketCount: number;
}

export class SalesPipelineModel implements ISalesPipelineModel {
    salesPipelineId: number = 0;
    companyId: number = 0;
    name: string = "";
    isDefault: boolean = false;
    isActive: boolean = true;
    sortOrder: number = 0;
    stages: ISalesPipelineStageModel[] = [];
    deletedStageIds: number[] = [];
    ticketCount: number = 0;
}

export interface ISalesTicketSourceModel {
    salesTicketSourceId: number;
    companyId: number;
    name: string;
    sortOrder: number;
    isActive: boolean;
    ticketCount: number;
}

export class SalesTicketSourceModel implements ISalesTicketSourceModel {
    salesTicketSourceId: number = 0;
    companyId: number = 0;
    name: string = "";
    sortOrder: number = 0;
    isActive: boolean = true;
    ticketCount: number = 0;
}

// The funnel positions a column can be mapped to, with the wording the settings page shows.
export interface ISalesPipelineStageKeyOptionModel {
    key: string;
    description: string;
    explanation: string;
}

// Which documents are allowed to create a ticket. Off means attach-only: the document still joins an
// opportunity it can be traced to, it just never mints a new card.
export interface ISalesTicketAutoCreateSettingsModel {
    createFromLeads: boolean;
    createFromQuotes: boolean;
    createFromLathamOrders: boolean;
    createFromInvoices: boolean;
    createFromJobs: boolean;
}

export interface ISalesPipelineSettingsModel {
    pipelines: ISalesPipelineModel[];
    sources: ISalesTicketSourceModel[];
    stageKeyOptions: ISalesPipelineStageKeyOptionModel[];
    autoCreateSettings: ISalesTicketAutoCreateSettingsModel;
}
